import { type BlobClient, createBlobClient } from '@jbroll/rowboat-client';
import { withSyncToken } from './syncToken';

const SYNC_BASE = import.meta.env.VITE_ROWBOAT_SYNC_BASE as string;
if (!SYNC_BASE) {
  throw new Error('VITE_ROWBOAT_SYNC_BASE is required');
}

export function blobApiBase(): string {
  // The rowboat server mounts media on the router-proxied sync prefix: /db/:id/api/sync/files
  // (server-buildapp.mjs: mountFileRoutes basePath). SYNC_BASE already ends in .../api/sync, so
  // the files base is SYNC_BASE + /files — NOT /api/files (that path 404s without CORS headers,
  // which the browser surfaces as a fetch TypeError, and every photo silently stays queued).
  return `${SYNC_BASE}/files`;
}

// A ~5 MB phone photo on a ~400 kbit/s mobile link takes about 100 s.
const BLOB_FETCH_TIMEOUT_MS = 120_000;

type FetchFn = (input: string, init?: RequestInit) => Promise<Response>;

// The rowboat client sets no timeout, so a stalled request would hold the drain (and every
// queued photo behind it) open forever. An abort lands in the queue's per-entry catch.
export function timedFetch(fetchFn: FetchFn, timeoutMs: number): FetchFn {
  return (input, init) => {
    const timeout = AbortSignal.timeout(timeoutMs);
    const signal = init?.signal ? AbortSignal.any([init.signal, timeout]) : timeout;
    return fetchFn(input, { ...init, signal });
  };
}

let client: BlobClient | null = null;

export function getBlobClient(): BlobClient {
  if (!client) {
    client = createBlobClient({
      apiBase: blobApiBase(),
      // Upload and sign carry the sync token; a signed read brings its own Authorization.
      fetchFn: withSyncToken(
        timedFetch((input, init) => fetch(input, init), BLOB_FETCH_TIMEOUT_MS),
      ),
    });
  }
  return client;
}

/** Test seam: drop the singleton so tests get a fresh client. */
export function __resetBlobClientForTests(): void {
  client = null;
}
